import { Injectable, PLATFORM_ID, Inject, Injector } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { TransferState, makeStateKey } from '@angular/core';
// store
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
// import 'rxjs/add/operator/take';
// import 'rxjs/add/operator/toPromise';
import { take, takeWhile, map, catchError } from 'rxjs/operators';

// services
import { ErrorHandler } from './error-handler.service';
import { environment } from '../../../environments/environment';
// models
import { AppState } from '../../booking/models/app-state.interface';
// api paths
import { API_PATH, SUBSCRIPTION_KEY, STORE_JSON_DATA, GET_JSON_DATA } from '../../shared/globals';
// cookie lib
import { Cookie } from 'ng2-cookies/ng2-cookies';
import { REQUEST } from '@nguniversal/express-engine/tokens';

interface DataStoreRequest {
  data: string;
  id?: string;
};

const STORE_KEY = makeStateKey('store');

@Injectable({
  providedIn: 'root',
})
export class DataStorageService {

  public storeID;
  public dataStore;
  public savedStoreSubject$: Subject<any> = new Subject();
  public dataSaved$: Subject<any> = new Subject();
  public addBookingFlightData = null;

  private _savedDataID;
  private _serverDataID = null;
  private _returnedString;
  private _parsedStoreData: any;
  private _firstLoad = true;
  private _firstLoadPromise = true;
  private _userCopy = null;
  private _saveDataSubscription: Subscription;

  constructor(
    private _appStore: Store<AppState>,
    private _http: HttpClient,
    private _errorService: ErrorHandler,
    private _injector: Injector,
    private _serverState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1)).subscribe(s => state = s);
    return state;
  }

  setServerCookieDataID(v) {
    this._serverDataID = v;
  }

  getServerCookieDataID() {

    if (isPlatformServer(this.platformId)) {
      const req = this._injector.get(REQUEST);
      const cookies = req.headers.cookie;
      const cArr = cookies.split('; ');
      const cStr = cArr.filter((v: string) => { if (v.includes('DataID')) { return v; } });
      const parsedStr = cStr.length > 0 ? cStr[0].split('=')[1] : null;
      this._serverDataID = parsedStr;
    }

    return this._serverDataID;
  }

  getCookieDataID() {
    if (isPlatformBrowser(this.platformId)) {
      return Cookie.get('DataID');
    } else {
      const req = this._injector.get(REQUEST);
      const cookies = req.headers.cookie;
      if (typeof cookies === 'undefined') { return null; }
      const cArr = cookies.split('; ');
      const cStr = cArr.filter((v: string) => { if (v.includes('DataID')) { return v; } });
      const parsedStr = cStr.length > 0 ? cStr[0].split('=')[1] : null;
      return parsedStr;
    }
  }

  setUserCookie(t: string, isAdmin: boolean) {

    if (t === 'undefined' || typeof t === 'undefined') { return; }

    // date value will be the expiry time in days
    let date: number;

    if (isAdmin) {
      date = 2;
    } else {
      date = 730;
    }

    const encodedString = btoa(t);
    this.setSharedCookie('DataT', encodedString, date);
    // Cookie.set('DataT', encodedString, date);
  }

  setSharedCookie(name: string, value: string, days = 2) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "; expires=" + date.toUTCString();

    if (window && window?.location.hostname.includes('.airportr.com')) {
      if (environment.envName === 'uat') {
        document.cookie = `${name+environment.envName}=${value}; domain=.airportr.com; path=/; Secure; SameSite=None;`;
      } else {
        document.cookie = `${name}=${value}; domain=.airportr.com; path=/; Secure; SameSite=None;`;
      }
    } else {
      document.cookie = `${name}=${value}; path=/;`;
    }
  }

  checkCookie(name) {
    let cookieName = name;
    if (environment.envName === 'uat') {
      cookieName = name + environment.envName;
    }
    if (isPlatformBrowser(this.platformId)) {
      const c = Cookie.get(cookieName);
      let value = null;

      if (c !== null) {
        value = atob(c);
      }

      return value = value === 'undefined' ? null : value;
    }
  }

  setCookie(cookieName: string, cookieValue: string, days = 720) {
    let cookieNameToSet = cookieName;
    if (environment.envName === 'uat') {
      cookieNameToSet = cookieName + environment.envName;
    }
    if (isPlatformBrowser(this.platformId)) {
      // this.setSharedCookie(cookieName, cookieValue, days);
      Cookie.set(cookieNameToSet, cookieValue, days);
    }
  }

  getCookie(cookieName: string) {
    let cookieNameToGet = cookieName;
    if (environment.envName === 'uat') {
      cookieNameToGet = cookieName + environment.envName;
    }
    if (isPlatformBrowser(this.platformId)) {
      return Cookie.get(cookieNameToGet);
    }
  }

  removeCookie(cookieName) {
    let cookieNameToRemove = cookieName;
    if (environment.envName === 'uat') {
      cookieNameToRemove = cookieName + environment.envName;
    }
    if (isPlatformBrowser(this.platformId)) {
      document.cookie = cookieNameToRemove + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      Cookie.delete(cookieNameToRemove);
    }
  }

  saveDataPromise() {
    console.log('saving data store on SERVER!!');
    const promise = new Promise((resolve, reject) => {
      const data = this.getState(this._appStore);
      const storeCopy = Object.assign({}, data);
      storeCopy.userReducer = null;
      storeCopy.threeDS2Reducer = null;

      const dataString = JSON.stringify(storeCopy);
      const request: DataStoreRequest = { data: dataString };
      let cookieSet = true;
      const cookieVal = this.getCookieDataID();

      if (cookieVal !== null) {
        request.id = cookieVal;
      } else {
        cookieSet = false;
      }

      // const headers = new HttpHeaders();
      // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      if (this._saveDataSubscription) { this._saveDataSubscription.unsubscribe(); }
      const headers = new HttpHeaders()
        .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

      this._http
        .post(API_PATH + STORE_JSON_DATA, request, { headers: headers })
        .toPromise()
        .then((returnedString: any) => {
          //this.dataStore = returnedString.json();
          this.dataStore = returnedString;
          this.storeID = this.dataStore.id;
          if (!cookieSet && isPlatformBrowser(this.platformId)) {
            // this.setSharedCookie('DataID', this.storeID, 1);
            Cookie.set('DataID', this.storeID, 1, '/');
          }
          this.dataSaved$.next(true);
          resolve(true);
        })
        .catch(err => {
          this.removeCookie('DataID');
          this._errorService.handleError(err);
          resolve(null);
          return;
        });
    });

    return promise;
  }

  saveData() {
    console.log('saving data store on SERVER!!@@');
    const data = this.getState(this._appStore);
    const storeCopy = Object.assign({}, data);
    storeCopy.userReducer = null;
    storeCopy.threeDS2Reducer = null;

    const dataString = JSON.stringify(storeCopy);
    const request: DataStoreRequest = { data: dataString };
    let cookieSet = true;
    const cookieVal = this.getCookieDataID();

    if (cookieVal !== null) {
      request.id = cookieVal;
    } else {
      cookieSet = false;
    }

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    if (this._saveDataSubscription) { this._saveDataSubscription.unsubscribe(); }
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._saveDataSubscription = this._http
      .post(API_PATH + STORE_JSON_DATA, request, { headers: headers })
      .subscribe(
        (returnedString: any) => {
          //this.dataStore = returnedString.json();
          this.dataStore = returnedString;
          this.storeID = this.dataStore.id;
          if (!cookieSet && isPlatformBrowser(this.platformId)) {
            // this.setSharedCookie('DataID', this.storeID, 1);
            Cookie.set('DataID', this.storeID, 1, '/');
          }
          this.dataSaved$.next(true);
        },
        err => {
          this.removeCookie('DataID');
          this._errorService.handleError(err);
          return;
        }
      );

  }

  getSavedData(forceData?: boolean) {

    const cookieVal = this.getCookieDataID();
    if (cookieVal !== null) {
      this._savedDataID = cookieVal;
    } else { return false; }

    this._parsedStoreData = this._serverState.get(STORE_KEY, null as any);

    if (!this._parsedStoreData || !this._firstLoad || forceData) {

      // const headers = new HttpHeaders();
      // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      const headers = new HttpHeaders().set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

      this._http
        .get(API_PATH + GET_JSON_DATA + this._savedDataID + '?cache=' + (Math.random() * 1000000), { headers: headers })
        .subscribe(
          (res: any) => {
            const returnedData = res;
            this._returnedString = returnedData.responseString;
            this._parsedStoreData = this.parseReturnedData(this._returnedString);
            this._serverState.set(STORE_KEY, this._parsedStoreData as any);
            this.savedStoreSubject$.next(this._parsedStoreData);
          },
          err => {
            this.removeCookie('DataID');
            this._errorService.handleError(err);
            return;
          }
        );

    } else {
      this._parsedStoreData.count = 1;
      this.savedStoreSubject$.next(this._parsedStoreData);
    }

    this._firstLoad = false;

  }

  getSavedDataPromise(forceData?: boolean) {

    const promise = new Promise((resolve, reject) => {

      const cookieVal = this.getCookieDataID();
      if (cookieVal !== null) {
        this._savedDataID = cookieVal;
      } else { return resolve(null); }

      this._parsedStoreData = this._serverState.get(STORE_KEY, null as any);
      // server store data is null
      // or first load is false
      // or force is true
      // get data from api
      if (!this._parsedStoreData || !this._firstLoadPromise || forceData) {

        // const headers = new HttpHeaders();
        // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

        const headers = new HttpHeaders().set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

        this._http
          .get(API_PATH + GET_JSON_DATA + this._savedDataID + '?cache=' + (Math.random() * 1000000), { headers: headers })
          .toPromise()
          .then(
            (res: any) => { // Success
              const returnedData = res;
              this._returnedString = returnedData.responseString;
              this._parsedStoreData = this.parseReturnedData(this._returnedString);
              this._serverState.set(STORE_KEY, this._parsedStoreData as any);
              this._firstLoadPromise = false;
              resolve(this._parsedStoreData);
            }
          )
          .catch(
            err => {
              this._errorService.handleError(err);
              this._serverState.set(STORE_KEY, null as any);
              resolve(null);
            }
          );

      } else {
        // else resolve data from server
        resolve(this._parsedStoreData);
      }
    });

    this._firstLoadPromise = false;
    return promise;

  }

  parseReturnedData(value: any) {
    const parsed = JSON.parse(value);
    return parsed;
  }

  // used to store flight data before its passed into addBooking
  // since the b/e can't handle any more data...
  setFlightData(d: any) {
    this.addBookingFlightData = d;
  }
  getFlightData() {
    return this.addBookingFlightData;
  }

}
